import React, { useEffect, useState } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloseIcon from "@mui/icons-material/Close";
import { Link as RouterLink } from "react-router-dom";
import userPlanService from "../services/userPlanService";

const SOON_DAYS = 7; // umbral de "por vencer"

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (d) =>
  d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });

// Devuelve { kind: "vencido" | "porvencer", date, days } o null si el plan está al día.
const evaluatePlan = (plan) => {
  const expiry = parseDate(plan?.expiry_date ?? plan?.expiryDate);
  if (!expiry) return null;

  const now = new Date();
  const msPerDay = 24 * 60 * 60 * 1000;
  const diffDays = Math.ceil((expiry - now) / msPerDay);

  if (diffDays < 0) return { kind: "vencido", date: expiry, days: Math.abs(diffDays) };
  if (diffDays <= SOON_DAYS) return { kind: "porvencer", date: expiry, days: diffDays };
  return null;
};

const PlanBanner = () => {
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let active = true;
    const userId = Number(sessionStorage.getItem("User"));
    if (!userId) return undefined;

    userPlanService
      .getUserPlans(userId)
      .then((res) => {
        if (!active) return;
        const plan = res?.items?.[0];
        setState(evaluatePlan(plan));
      })
      .catch(() => {
        // Sin plan o sin acceso (p. ej. usuario no-owner): no se muestra banner.
      });

    return () => {
      active = false;
    };
  }, []);

  if (!state || dismissed) return null;

  const vencido = state.kind === "vencido";
  const palette = vencido
    ? { bg: "#fef2f2", border: "#fecaca", fg: "#b91c1c", icon: "#dc2626" }
    : { bg: "#fffbeb", border: "#fde68a", fg: "#92400e", icon: "#d97706" };

  const mensaje = vencido
    ? `Tu plan venció el ${formatDate(state.date)}. Renovalo para seguir operando sin interrupciones.`
    : `Tu plan vence el ${formatDate(state.date)} (en ${state.days} ${state.days === 1 ? "día" : "días"}). Renovalo para no perder acceso.`;

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: { xs: 2, md: 3 },
        py: 1.25,
        backgroundColor: palette.bg,
        borderBottom: `1px solid ${palette.border}`,
      }}
    >
      <WarningAmberRoundedIcon sx={{ color: palette.icon, fontSize: 22, flexShrink: 0 }} />
      <Typography sx={{ flexGrow: 1, fontSize: "0.85rem", fontWeight: 500, color: palette.fg }}>
        {mensaje}
      </Typography>
      <Button
        component={RouterLink}
        to="/plans"
        variant="contained"
        disableElevation
        sx={{
          flexShrink: 0,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.8rem",
          borderRadius: "8px",
          px: 2,
          backgroundColor: palette.fg,
          "&:hover": { backgroundColor: palette.icon },
        }}
      >
        Renovar plan
      </Button>
      {/* Solo se puede descartar el aviso de "por vencer"; el de plan vencido permanece. */}
      {!vencido && (
        <IconButton
          size="small"
          onClick={() => setDismissed(true)}
          aria-label="Cerrar aviso"
          sx={{ flexShrink: 0, color: palette.fg }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default PlanBanner;
